/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, Modal, IconButton, CircularProgress, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CollectionsIcon from '@mui/icons-material/Collections';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface PhotoItem {
  id: string;
  title: string;
  imageUrl: string;
  category?: string;
  order?: number;
}

export default function GalleryPhotos() {
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'gallery_photos'));
        const fetched: PhotoItem[] = [];
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.imageUrl) {
            fetched.push({ id: docSnap.id, ...data } as PhotoItem);
          }
        });
        setPhotos(fetched.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      } catch (error) {
        console.error('Error loading gallery photos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPhotos();
  }, []);

  const handleImageClick = (index: number) => { setCurrentIndex(index); setPreviewOpen(true); };
  const handleClosePreview = () => setPreviewOpen(false);
  const handlePrevImage = () => setCurrentIndex(currentIndex > 0 ? currentIndex - 1 : photos.length - 1);
  const handleNextImage = () => setCurrentIndex(currentIndex < photos.length - 1 ? currentIndex + 1 : 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Photo Gallery — Kottayil Temple | ചിത്രങ്ങൾ" description="Beautiful photos of Kottayil Temple (Kotta-Kovilakam Kshethram) — architecture, rituals, festivals, Kottayil Kulam, and sacred moments." path="/gallery/photos" keywords="Kottayil temple photos, temple images, photo gallery, Kottayil kulam photos, Muchukunnu temple images, കൊട്ടയിൽ ക്ഷേത്രം ചിത്രങ്ങൾ, sacred photos Kerala" />
      <PageBanner title="Photo Gallery" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{ borderRadius: '24px', overflow: 'hidden', mb: 3.5, boxShadow: '0 20px 60px rgba(236,72,153,0.15)' }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #831843 0%, #ec4899 55%, #fbcfe8 100%)',
                px: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 5 }, pb: { xs: 5, sm: 6 },
                position: 'relative', overflow: 'hidden',
              }}>
                <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
                  }}>
                    <PhotoCameraIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: '#fff', fontSize: { xs: '1.8rem', sm: '2.4rem' }, mb: 1.5, lineHeight: 1.1 }}>
                    Photo Gallery
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.7, maxWidth: 500, mb: 2.5 }}>
                    {photos.length} photos capturing the divine atmosphere, festivals, and daily rituals of our temple.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip icon={<PhotoCameraIcon sx={{ fontSize: 16, color: '#ec4899 !important' }} />} label="Photos" size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#ec4899', fontWeight: 700, fontSize: '0.7rem', height: 28, borderRadius: '14px' }} />
                    <Chip icon={<CollectionsIcon sx={{ fontSize: 16, color: '#fff !important' }} />} label={`${photos.length} Images`} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 28, borderRadius: '14px', backdropFilter: 'blur(10px)' }} />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* PHOTO GRID */}
            <Grid container spacing={2}>
              {photos.map((photo, index) => (
                <Grid item xs={6} sm={4} key={index}>
                  <Box
                    onClick={() => handleImageClick(index)}
                    sx={{
                      borderRadius: '20px', overflow: 'hidden', cursor: 'pointer',
                      position: 'relative',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                      transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.12)',
                        '& .phOverlay': { opacity: 1 },
                      },
                    }}
                  >
                    <Box component="img" src={photo.imageUrl} alt={photo.title} loading="lazy"
                      sx={{ width: '100%', height: { xs: 160, sm: 220 }, objectFit: 'cover', display: 'block' }}
                    />
                    <Box className="phOverlay" sx={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent 60%)',
                      opacity: 0, transition: 'opacity 300ms ease',
                      display: 'flex', alignItems: 'flex-end', p: 1.5,
                    }}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.72rem' }}>
                        {photo.title}
                      </Typography>
                    </Box>
                    <Box sx={{ position: 'absolute', top: 10, right: 10, width: 26, height: 26, borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PhotoCameraIcon sx={{ fontSize: 13, color: '#fff' }} />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* FOOTER NOTE */}
            <Box sx={{
              mt: 3.5, borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              boxShadow: '0 8px 32px rgba(15,23,42,0.3)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ px: { xs: 3, sm: 3.5 }, py: 3, position: 'relative', zIndex: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>Browse Temple Memories</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, maxWidth: 500, mx: 'auto' }}>
                  Capturing divine moments, festivals, and the serene beauty of our sacred space.
                </Typography>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="gallery" activePath="/gallery/photos" />
          </Grid>
        </Grid>
      </Container>

      {/* Image Preview Modal */}
      <Modal open={previewOpen} onClose={handleClosePreview} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.92)' }}>
        <Box sx={{ position: 'relative', outline: 'none' }}>
          <IconButton onClick={handleClosePreview} sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'rgba(236,72,153,0.9)', color: '#fff', '&:hover': { bgcolor: '#ec4899' }, zIndex: 1 }}>
            <CloseIcon />
          </IconButton>
          {photos.length > 1 && (
            <IconButton onClick={handlePrevImage} sx={{ position: 'absolute', left: { xs: 8, md: 16 }, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(236,72,153,0.8)', color: '#fff', '&:hover': { bgcolor: '#ec4899' } }}>
              <NavigateBeforeIcon />
            </IconButton>
          )}
          {photos.length > 1 && (
            <IconButton onClick={handleNextImage} sx={{ position: 'absolute', right: { xs: 8, md: 16 }, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(236,72,153,0.8)', color: '#fff', '&:hover': { bgcolor: '#ec4899' } }}>
              <NavigateNextIcon />
            </IconButton>
          )}
          <Box component="img" src={photos[currentIndex]?.imageUrl} alt={photos[currentIndex]?.title}
            sx={{ maxWidth: { xs: '95vw', sm: '90vw' }, maxHeight: '85dvh', objectFit: 'contain', borderRadius: '12px', border: '3px solid rgba(236,72,153,0.5)' }}
          />
          {photos.length > 1 && (
            <Box sx={{
              position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #ec4899, #db2777)',
              color: '#fff', px: 2.5, py: 0.8, borderRadius: '12px',
              fontWeight: 700, fontSize: '0.82rem',
            }}>
              {currentIndex + 1} / {photos.length}
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
}
